import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useExperience } from '../../contexts/ExperienceContext';
import { ctaOptions, type CtaKind } from '../../data/journeys';
import { needById, needs } from '../../data/needs';
import { brand } from '../../data/palette';
import { NextStepPanel } from '../recommend/NextStepPanel';
import { ProductPanel } from '../ProductPanel';
import { Advance } from '../ui/Advance';
import { ChoiceList } from '../ui/ChoiceList';
import { Eyebrow, RevealText, Stage, StageTitle } from '../ui/Stage';
import type { Choice, NeedId } from '../../types/filtek';
const ease = [0.16, 1, 0.3, 1] as const;
const needChoices: Choice[] = needs.map(need => ({
  id: need.id,
  label: need.label,
  detail: need.resolution,
  need: need.id
}));

/** The consultative moment: everything said so far, resolved into one focus. */
export function RecommendStage() {
  const {
    signals,
    update,
    push,
    next,
    direction,
    emphasis,
    memory
  } = useExperience();
  const [cta, setCta] = useState<CtaKind | undefined>();
  const focus = signals.focusNeed;
  const need = needById(focus);
  const recap = memory.filter(entry => ['Priority', 'Recognition', 'Current use', 'Other brand used for', 'Explored'].includes(entry.label));
  const chooseCta = (kind: CtaKind, label: string) => {
    setCta(kind);
    update({
      cta: kind
    });
    push('contentExplored', label);
  };
  return <Stage direction={direction} align="start" className="min-h-screen py-28 sm:py-32" labelledBy="recommend-title" data-id="element-301" data-mp="1f9sfcmuq4n0q">
      <AnimatePresence mode="wait" data-id="element-302" data-mp="15cfcsl5jr43z">
        {!focus ? <motion.div key="ask" exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.5,
        ease
      }} className="grid gap-12 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:items-start lg:gap-20" data-id="element-303" data-mp="15dytudz11j6t">
            <div className="lg:sticky lg:top-36" data-id="element-304" data-mp="34q1is3qf75rv">
              <Eyebrow accent={brand.green} data-id="element-305" data-mp="34oqem2t2bw08">Last question</Eyebrow>
              <StageTitle id="recommend-title" size="display-sm" className="mt-7 max-w-md" data-id="element-306" data-mp="339dy1ftrpdeh">
                Where could Filtek make the biggest difference in your practice?
              </StageTitle>
              <p className="mt-7 max-w-sm text-base font-light leading-relaxed text-mute" data-id="element-307" data-mp="2tb7wx0fscbao">
                We will take you straight to that part of the family — and nothing else.
              </p>
            </div>

            <ChoiceList choices={needChoices} accent={brand.green} onSelect={choice => update({
          focusNeed: choice.id as NeedId
        })} data-id="element-308" data-mp="3lyc9bqh7jrwh" />
          </motion.div> : <motion.div key="summary" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} data-id="element-309" data-mp="3c6y5j6i845xj">
            <div className="max-w-4xl" data-id="element-310" data-mp="387766eemshhe">
              <Eyebrow accent={need.accent} data-id="element-311" data-mp="8jpg3lfeytgb">Based on what you&apos;ve told us…</Eyebrow>
              <h2 id="recommend-title" className="display-tight mt-9 text-[2rem] font-light text-enamel sm:text-[2.9rem] lg:text-[3.4rem]" data-id="element-312" data-mp="3t8wfjl02bli">
                <RevealText text={signals.priority ? `${emphasis.charAt(0).toUpperCase()}${emphasis.slice(1)} matters most to you.` : `${need.label} is where Filtek can make the difference.`} delay={0.15} data-id="element-313" data-mp="1mibu8iqkyyxg" />
              </h2>

              {recap.length > 0 && <motion.ul initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.8,
            delay: 0.8
          }} className="mt-10 flex flex-wrap gap-x-10 gap-y-4" data-id="element-314" data-mp="25d0i1sm7aspe">
                  {recap.map(entry => <li key={entry.label} data-id="element-315" data-mp="2o5cnio85ia3r">
                      <p className="label-mono text-[9px] text-mute/70" data-id="element-316" data-mp="2o6noyhe4j5rp">{entry.label}</p>
                      <p className="mt-1.5 text-sm font-light text-enamel" data-id="element-317" data-mp="2t5h40e9274tz">{entry.value}</p>
                    </li>)}
                </motion.ul>}
            </div>

            <motion.div initial={{
          opacity: 0,
          y: 22
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 1,
          ease
        }} className="mt-16 grid gap-12 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-16" data-id="element-318" data-mp="2ajdt892jyk5a">
              <div data-id="element-319" data-mp="1wi1eu231iqli">
                <p className="label-mono text-[10px] text-mute" data-id="element-320" data-mp="1wjmajv13cfky">Your Filtek area</p>
                <p className="mt-4 text-2xl font-light tracking-[-0.02em] sm:text-3xl" style={{
              color: need.accent
            }} data-id="element-321" data-mp="1rkjbcr7sl3kw">
                  {need.label}
                </p>
                <p className="mt-5 max-w-lg text-base font-light leading-relaxed text-mute" data-id="element-322" data-mp="21gcp2dx8z349">
                  {need.resolution}
                </p>
                <div className="mt-9" data-id="element-323" data-mp="3cxcpxxxnuq9w">
                  <ProductPanel productId={need.productId} companionId={need.companionId} accent={need.accent} eyebrow="Recommended within the family" data-id="element-324" data-mp="3cwb4czj0qldx" />
                </div>
              </div>

              <div data-id="element-325" data-mp="3bgyxj49izh5d">
                <p className="label-mono text-[10px] text-mute" data-id="element-326" data-mp="3bhi81zwuj39d">Where would you like to go?</p>
                <ul className="mt-5 border-t border-enamel/10" data-id="element-327" data-mp="3fi0xjnb9nx8e">
                  {ctaOptions.map(option => {
                const isActive = cta === option.id;
                return <li key={option.id} className="border-b border-enamel/10" data-id="element-328" data-mp="c0agpz2hs522">
                        <button type="button" onClick={() => chooseCta(option.id, option.label)} aria-pressed={isActive} className="group flex w-full items-center justify-between gap-6 py-4 text-left" data-id="element-329" data-mp="3ue04uvqjxtch">
                          <span className="text-lg font-light transition-all duration-500 group-hover:translate-x-1" style={{
                      color: isActive ? need.accent : 'var(--enamel)'
                    }} data-id="element-330" data-mp="uteadkfpmhwo">
                            {option.label}
                          </span>
                          <span className="label-mono shrink-0 text-[9px] text-mute/70" data-id="element-331" data-mp="qo0ojxmdkqmh">
                            {option.detail}
                          </span>
                        </button>
                      </li>;
              })}
                </ul>
              </div>
            </motion.div>

            <AnimatePresence mode="wait" data-id="element-332" data-mp="cwvwbglc70m6">
              {cta && <motion.div key={cta} className="mt-14" data-id="element-333" data-mp="7xjamz67bzl6">
                  <NextStepPanel kind={cta} need={need} data-id="element-334" data-mp="7yuainwj0n70" />
                </motion.div>}
            </AnimatePresence>

            <div className="mt-16 flex flex-wrap items-center justify-between gap-6 border-t border-enamel/10 pt-8" data-id="element-335" data-mp="3u4hiztxnsz20">
              <button type="button" onClick={() => {
            setCta(undefined);
            update({
              focusNeed: undefined
            });
          }} className="label-mono text-[10px] text-mute transition-colors hover:text-enamel" data-id="element-336" data-mp="3k12gjboo4gj3">
                Choose a different area
              </button>
              <Advance label="See the whole family" onClick={next} accent={need.accent} data-id="element-337" data-mp="2pt7qk98cxyk" />
            </div>
          </motion.div>}
      </AnimatePresence>
    </Stage>;
}